import React, { useState, useEffect, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import mammoth from "mammoth";
import "./InlineDocumentViewer.css";

// pdfjs.GlobalWorkerOptions.workerSrc = new URL(
//     "pdfjs-dist/build/pdf.worker.min.mjs",
//     import.meta.url
// ).toString();
// pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.4.168/pdf.worker.min.mjs`;
// ✅ FIXED
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

const ZOOM_LEVELS = [0.5, 0.75, 1.0, 1.25, 1.5, 1.75, 2.0, 2.5, 3.0];

type ViewerState =
    | { status: "idle" }
    | { status: "loading" }
    | { status: "pdf-ready"; blobUrl: string }
    | { status: "doc-ready"; htmlContent: string }
    | { status: "error"; message: string };

interface InlineDocumentViewerProps {
    fileUrl: string;
    fileType: "pdf" | "doc" | "docx";
    height?: string | number;
}

const InlineDocumentViewer: React.FC<InlineDocumentViewerProps> = ({
    fileUrl, fileType, height = "calc(100vh - 200px)"
}) => {
    const [viewerState, setViewerState] = useState<ViewerState>({ status: "loading" });
    const [numPages, setNumPages] = useState<number>(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>(800);

    // Toolbar state
    const [fitToWidth, setFitToWidth] = useState(true);
    const [zoomIndex, setZoomIndex] = useState(2); // index 2 = 1.0 → 100%
    const [currentPage, setCurrentPage] = useState(1);
    const [pageInput, setPageInput] = useState("1");
    const pageRefs = useRef<(HTMLDivElement | null)[]>([]);

    // Track scrollable container width for fit-to-width rendering
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const ro = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setContainerWidth(entry.contentRect.width || 800);
            }
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // Fetch and prepare the document
    useEffect(() => {
        if (!fileUrl) return;

        let aborted = false;
        let blobUrl: string | null = null;

        setViewerState({ status: "loading" });
        setNumPages(0);
        setCurrentPage(1);
        setPageInput("1");

        const doFetch = async () => {
            const res = await fetch(fileUrl, { mode: "cors" });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            if (fileType === "pdf") {
                const blob = await res.blob();
                if (aborted) return;
                blobUrl = URL.createObjectURL(blob);
                setViewerState({ status: "pdf-ready", blobUrl });
            } else {
                const arrayBuffer = await res.arrayBuffer();
                if (aborted) return;
                const { value: htmlContent } = await mammoth.convertToHtml(
                    { arrayBuffer },
                    {
                        convertImage: mammoth.images.imgElement(async (image: any) => {
                            const base64 = await image.readAsBase64String();
                            return { src: `data:${image.contentType};base64,${base64}` };
                        }),
                    }
                );
                if (aborted) return;
                setViewerState({ status: "doc-ready", htmlContent });
            }
        };

        doFetch().catch((err: unknown) => {
            if (aborted) return;
            const message = err instanceof Error ? err.message : "Failed to load file.";
            setViewerState({ status: "error", message });
        });

        return () => {
            aborted = true;
            if (blobUrl) URL.revokeObjectURL(blobUrl);
        };
    }, [fileUrl, fileType]);

    // Block Ctrl+P / Cmd+P when a PDF is loaded to prevent unauthorised printing
    useEffect(() => {
        if (viewerState.status !== "pdf-ready") return;
        const handler = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "p") {
                e.preventDefault();
                e.stopPropagation();
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [viewerState.status]);

    // Keep page input in sync with the page currently most visible in the viewport
    useEffect(() => {
        if (numPages === 0 || !containerRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                let best: { page: number; ratio: number } = { page: 1, ratio: 0 };
                entries.forEach((entry) => {
                    const idx = pageRefs.current.findIndex((el) => el === entry.target);
                    if (idx !== -1 && entry.intersectionRatio > best.ratio) {
                        best = { page: idx + 1, ratio: entry.intersectionRatio };
                    }
                });
                if (best.ratio > 0) {
                    setCurrentPage(best.page);
                    setPageInput(String(best.page));
                }
            },
            { root: containerRef.current, threshold: [0.25, 0.5, 0.75] }
        );

        pageRefs.current.slice(0, numPages).forEach((el) => {
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, [numPages]); // eslint-disable-line react-hooks/exhaustive-deps

    const scrollToPage = (page: number) => {
        const clamped = Math.max(1, Math.min(page, numPages));
        pageRefs.current[clamped - 1]?.scrollIntoView({ behavior: "smooth", block: "start" });
        setCurrentPage(clamped);
        setPageInput(String(clamped));
    };

    const commitPageInput = () => {
        const n = parseInt(pageInput, 10);
        if (!isNaN(n) && n >= 1 && n <= numPages) {
            scrollToPage(n);
        } else {
            setPageInput(String(currentPage));
        }
    };

    const zoomIn = () => {
        setFitToWidth(false);
        setZoomIndex((i) => Math.min(i + 1, ZOOM_LEVELS.length - 1));
    };

    const zoomOut = () => {
        setFitToWidth(false);
        setZoomIndex((i) => Math.max(i - 1, 0));
    };

    const toggleFitToWidth = () => setFitToWidth((f) => !f);

    const centeredStyle: React.CSSProperties = {
        width: "100%",
        maxHeight: height,
        minHeight: 400,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    };

    if (viewerState.status === "loading" || viewerState.status === "idle") {
        return <div style={centeredStyle}><span>Loading document…</span></div>;
    }

    if (viewerState.status === "error") {
        return (
            <div style={centeredStyle}>
                <span>Unable to display this document. Please download the file to view it.</span>
                <br />
                <a href={fileUrl} download target="_blank" rel="noreferrer">Download file</a>
            </div>
        );
    }

    if (viewerState.status === "pdf-ready") {
        const isLoaded = numPages > 0;
        return (
            <div style={{ width: "100%", display: "flex", flexDirection: "column", maxHeight: height, minHeight: 400, overflow: "hidden" }}>
                <div className="idv-toolbar">
                    <button
                        onClick={() => scrollToPage(currentPage - 1)}
                        disabled={!isLoaded || currentPage <= 1}
                        title="Previous page"
                    >
                        ‹
                    </button>
                    <input
                        type="text"
                        value={pageInput}
                        disabled={!isLoaded}
                        onChange={(e) => setPageInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") commitPageInput(); }}
                        onBlur={commitPageInput}
                    />
                    <span className="idv-total">/ {isLoaded ? numPages : "–"}</span>
                    <button
                        onClick={() => scrollToPage(currentPage + 1)}
                        disabled={!isLoaded || currentPage >= numPages}
                        title="Next page"
                    >
                        ›
                    </button>
                    <span className="idv-divider" />
                    <button onClick={zoomOut} disabled={!isLoaded || fitToWidth || zoomIndex <= 0} title="Zoom out">−</button>
                    <span className="idv-zoom-label">
                        {!isLoaded ? "–" : fitToWidth ? "Fit W" : `${Math.round(ZOOM_LEVELS[zoomIndex] * 100)}%`}
                    </span>
                    <button onClick={zoomIn} disabled={!isLoaded || fitToWidth || zoomIndex >= ZOOM_LEVELS.length - 1} title="Zoom in">+</button>
                    <button
                        onClick={toggleFitToWidth}
                        disabled={!isLoaded}
                        className={fitToWidth ? "idv-btn-active" : ""}
                        title="Fit to width"
                    >
                        Fit W
                    </button>
                </div>
                <div
                    ref={containerRef}
                    style={{ flex: 1, overflowY: "auto", overflowX: "hidden", userSelect: "none" }}
                    onContextMenu={(e) => e.preventDefault()}
                >
                    <Document
                        file={viewerState.blobUrl}
                        onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                        onLoadError={() => setViewerState({ status: "error", message: "Failed to render PDF." })}
                    >
                        {Array.from({ length: numPages }, (_, i) => (
                            <div key={`page_${i + 1}`} ref={(el) => { pageRefs.current[i] = el; }}>
                                <Page
                                    pageNumber={i + 1}
                                    {...(fitToWidth ? { width: containerWidth } : { scale: ZOOM_LEVELS[zoomIndex] })}
                                    renderAnnotationLayer={false}
                                    renderTextLayer={false}
                                />
                            </div>
                        ))}
                    </Document>
                </div>
            </div>
        );
    }

    // doc-ready
    return (
        <div
            className="docx-content"
            style={{
                width: "100%",
                maxHeight: height,
                minHeight: 400,
                overflowY: "auto",
            }}
            dangerouslySetInnerHTML={{ __html: viewerState.htmlContent }}
        />
    );
};

export default InlineDocumentViewer;
